import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { AlertTriangle, CreditCard, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Full-screen lock shown when an organization's subscription is past_due.
 * Blocks every app route except itself; the only way forward is to retry
 * payment (which fires off a fresh PayPal subscription) or log out.
 */
export default function PaymentRequired() {
    const { user, currentOrg, logout, refreshOrgs } = useAuth();
    const [busy, setBusy] = useState(false);

    const retry = async () => {
        setBusy(true);
        try {
            const plan = currentOrg?.plan && currentOrg.plan !== "free" ? currentOrg.plan : "pro";
            const { data } = await api.post("/billing/paypal/subscription", { plan });
            toast.info("Redirecting to PayPal...");
            window.location.href = data.approve_url;
        } catch (e) {
            toast.error(formatApiError(e));
            setBusy(false);
        }
    };

    const refresh = async () => {
        setBusy(true);
        try { await refreshOrgs(); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-ocean-mesh grid place-items-center p-6">
            <div className="w-full max-w-lg bg-white rounded-3xl border border-coral/30 shadow-card p-8">
                <div className="flex items-center justify-between">
                    <Logo />
                    <Button variant="ghost" size="sm" onClick={logout} data-testid="past-due-logout" className="text-muted-foreground">
                        <LogOut className="h-4 w-4 mr-1.5" /> Log out
                    </Button>
                </div>

                <div className="mt-6 inline-flex items-center gap-2 bg-coral/10 text-coral-dark text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">
                    <AlertTriangle className="h-3.5 w-3.5" /> Payment required
                </div>

                <h1 className="font-heading text-3xl text-ocean-700 mt-4 leading-tight">
                    Your last subscription payment didn't go through
                </h1>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                    Hi {user?.email} — PayPal let us know the last charge for
                    <span className="font-medium text-ocean-700"> {currentOrg?.name} </span>
                    was declined. To keep your books safe, we've temporarily locked access to the rest of the app.
                    Nothing has been deleted and no data has changed — you'll be right back in the moment a payment clears.
                </p>

                <div className="mt-6 rounded-2xl bg-sand-100 p-4 text-sm">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Current plan</div>
                    <div className="text-ocean-700 font-medium mt-1">
                        {(currentOrg?.plan || "free").toUpperCase()}
                        <span className="ml-2 text-coral-dark text-xs">past due</span>
                    </div>
                </div>

                <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
                    <Button
                        onClick={retry}
                        disabled={busy}
                        className="flex-1 bg-coral hover:bg-coral-dark text-white h-12 rounded-xl"
                        data-testid="past-due-retry-btn"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1.5" /> Update payment method</>}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={refresh}
                        disabled={busy}
                        className="h-12 rounded-xl border-sand-300"
                        data-testid="past-due-refresh-btn"
                    >
                        I just paid — refresh
                    </Button>
                </div>

                <p className="text-[11px] text-muted-foreground mt-4 text-center leading-relaxed">
                    If you think this is a mistake, reply to the billing email we just sent you
                    or email support and we'll sort it in minutes.
                </p>
            </div>
        </div>
    );
}
